import * as React from 'react'
import { ChevronDown, ChevronUp, X } from 'lucide-react'

import { CopyChip } from '@/components/CopyChip'
import { FactsLine } from '@/components/FactsLine'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { SheetClose, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import type { PatientRecord } from '@/features/patients/types'
import {
  cityAndState,
  clientAgeYears,
  clientInitials,
  displayCase,
} from '@/features/patients/utils/clientDisplay'
import { headerSourceIds } from '@/features/patients/utils/sourceIds'
import { formatDateOfBirth, patientFullName } from './PatientsColumns'

interface ClientSheetHeaderProps {
  patient: PatientRecord
  prevId: string | undefined
  nextId: string | undefined
  onPrev: () => void
  onNext: () => void
  /** Jump to the Identifiers tab from the "+N more" chip. */
  onShowIdentifiers: () => void
}

/** Insurance reads "Medicare · Partnership" when both are present. */
function insuranceLabel(patient: PatientRecord): string | null {
  const parts = [patient.insurance, patient.primary_insurance]
    .map((part) => part?.trim())
    .filter((part): part is string => !!part)
    .map(displayCase)
  // The two columns often hold the same payer; show it once.
  return [...new Set(parts)].join(' · ') || null
}

/**
 * Identity line: the facts you read back to confirm you have the right person,
 * then where they live. City and state are read from the one-line address
 * when it can be split reliably; county is its own field.
 */
function identityFacts(patient: PatientRecord): string[] {
  const age = clientAgeYears(patient.date_of_birth)
  const dob = formatDateOfBirth(patient.date_of_birth)
  return [
    age !== null ? `${age} yrs` : null,
    patient.gender,
    dob ? `DOB ${dob}` : null,
    patient.language,
    // One fact, as in the design, so a wrap never splits city from county.
    [
      cityAndState(patient.address),
      patient.county_name && displayCase(patient.county_name),
    ]
      .filter(Boolean)
      .join(' · '),
  ]
    .map((fact) => fact?.trim())
    .filter((fact): fact is string => !!fact)
    .map(displayCase)
}

export function ClientSheetHeader({
  patient,
  prevId,
  nextId,
  onPrev,
  onNext,
  onShowIdentifiers,
}: ClientSheetHeaderProps) {
  const name = displayCase(patientFullName(patient)) || 'Client Details'
  const facts = identityFacts(patient)
  const insurance = insuranceLabel(patient)
  const { shown, remaining } = React.useMemo(
    () => headerSourceIds(patient.source_ids),
    [patient.source_ids]
  )

  return (
    <SheetHeader className="gap-0 px-4 pt-4 pb-2">
      <div className="flex items-start gap-3">
        <Avatar size="lg" className="mt-0.5">
          <AvatarFallback className="border-neutral-subtle-border border font-medium">
            {clientInitials(patient)}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1">
          {/* One line, as in the design: the payer shortens (full text on
              hover) and the name never gives way to it. */}
          <SheetTitle className="flex min-w-0 items-center gap-2 text-left text-lg leading-7">
            <span className="max-w-full shrink-0 truncate" title={name}>
              {name}
            </span>
            {patient.is_deceased ? (
              <Badge variant="destructive">Deceased</Badge>
            ) : patient.is_active === false ? (
              <Badge variant="secondary">Inactive</Badge>
            ) : patient.is_active ? (
              <Badge variant="success" className="shrink-0">
                <span
                  aria-hidden
                  className="size-1.5 rounded-full bg-current"
                />
                Active
              </Badge>
            ) : null}
            {insurance ? (
              <Badge className="min-w-0 shrink" title={insurance}>
                <span className="truncate">{insurance}</span>
              </Badge>
            ) : null}
          </SheetTitle>

          <FactsLine facts={facts} />
        </div>

        <Button
          size="icon-sm"
          variant="ghost"
          disabled={!prevId}
          onClick={onPrev}
          aria-label="Previous client"
          title="Previous client (↑)">
          <ChevronUp />
        </Button>
        <Button
          size="icon-sm"
          variant="ghost"
          disabled={!nextId}
          onClick={onNext}
          aria-label="Next client"
          title="Next client (↓)">
          <ChevronDown />
        </Button>
        <SheetClose asChild>
          <Button size="icon-sm" variant="ghost">
            <X />
            <span className="sr-only">Close</span>
          </Button>
        </SheetClose>
      </div>

      {shown.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {shown.map((entry) => (
            <CopyChip
              key={`${entry.system}:${entry.value}`}
              label={entry.shortLabel}
              value={entry.value}
            />
          ))}
          {remaining > 0 ? (
            <Button
              size="xs"
              variant="ghost"
              className="border-neutral-subtle-border text-primary-low-contrast border border-dashed font-normal"
              onClick={onShowIdentifiers}>
              +{remaining} more
            </Button>
          ) : null}
        </div>
      ) : null}
    </SheetHeader>
  )
}
